import React, { memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import ModalContainer, { ButtonGroup } from '@/shared/components/modal/ModalContainer';
import StyledButton from '@/shared/components/atoms/StyledButton';
import theme from '@/styles/theme';
import type { ScanParticipant } from '../types/types';

type ScanSuccessModalProps = {
	participant: ScanParticipant;
	checkedInAt: string; // '오전 8:30'
	onUndo: () => void;
	onNext: () => void;
};

const UNDO_SECONDS = 5;

// 체크인 완료 성공 모달 (실행 취소 카운트다운 포함)
const ScanSuccessModal = memo<ScanSuccessModalProps>(({ participant, checkedInAt, onUndo, onNext }) => {
	const [remainSeconds, setRemainSeconds] = useState(UNDO_SECONDS);

	// 실행 취소 카운트다운 → 0초 도달 시 다음 스캔으로 자동 진행
	useEffect(() => {
		if (remainSeconds <= 0) {
			onNext();
			return;
		}

		const timerId = setTimeout(() => setRemainSeconds((prev) => prev - 1), 1000);
		return () => clearTimeout(timerId);
	}, [remainSeconds, onNext]);

	return (
		<ModalContainer isFixed={true} maxWidth={480} title="체크인 완료">
			<Body>
				<SuccessBadge>✓ 체크인 완료</SuccessBadge>
				<Name>{participant.name}</Name>
				<EnglishName>{participant.englishName}</EnglishName>
				<BibRow>
					<Bib>{participant.bibNumber}</Bib>
					<Course>{participant.courseName}</Course>
				</BibRow>
				<CheckedInAt>{checkedInAt}</CheckedInAt>
			</Body>
			<ButtonGroup>
				<StyledButton width={200} fontColor={theme.colors.deepGrayFontColor} onClick={onUndo}>
					↵ 실행 취소 ({remainSeconds}초)
				</StyledButton>
				<StyledButton
					width={200}
					fontColor={theme.colors.whiteColor}
					bgColor={theme.colors.activeBlue}
					border="none"
					onClick={onNext}>
					다음 스캔 진행하기
				</StyledButton>
			</ButtonGroup>
		</ModalContainer>
	);
});

ScanSuccessModal.displayName = 'ScanSuccessModal';

export default ScanSuccessModal;

const Body = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 8px 0;
`;
const SuccessBadge = styled.div`
	font-size: 14px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	padding: 8px 16px;
	border-radius: 6px;
	color: #2f9e5f;
	background-color: #e6f6ec;
	margin-bottom: 24px;
`;
const Name = styled.div`
	font-size: 32px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
`;
const EnglishName = styled.div`
	font-size: 15px;
	color: ${({ theme }) => theme.colors.deepGrayFontColor};
	margin-top: 8px;
`;
const BibRow = styled.div`
	display: flex;
	align-items: baseline;
	gap: 12px;
	margin-top: 20px;
`;
const Bib = styled.div`
	font-size: 28px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	color: ${({ theme }) => theme.colors.activeBlue};
`;
const Course = styled.div`
	font-size: 20px;
	font-family: PretendardMedium, AppleSDGothicNeoMedium, sans-serif;
`;
const CheckedInAt = styled.div`
	font-size: 14px;
	color: ${({ theme }) => theme.colors.deepGrayFontColor};
	margin-top: 16px;
`;
